import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CableRoundedIcon from '@mui/icons-material/CableRounded';
import RouterRoundedIcon from '@mui/icons-material/RouterRounded';

import { communicationEquipmentApi } from '../../api/communicationEquipment';

const sourceOptions = [
  ['passport', 'Паспорт прибора'],
  ['nameplate', 'Маркировка на приборе'],
  ['catalog', 'Каталог оборудования'],
  ['calibration', 'Контрольная проверка'],
  ['operator', 'Введено оператором'],
];

const unitLabels = {
  l: 'литров',
  m3: 'м³',
  kwh: 'кВт·ч',
  gcal: 'Гкал',
};

const emptyModule = {
  name: '',
  manufacturer: '',
  model: '',
  serial_number: '',
  uplink_transport: 'lorawan',
  channel_count: 4,
  dev_eui: '',
  network_address: '',
  notes: '',
};

function defaultUnit(device) {
  const type = String(device?.system_type || '').toLowerCase();
  if (type.includes('элект')) return 'kwh';
  if (type.includes('тепл') || type.includes('отоп')) return 'gcal';
  return 'l';
}

function formatNumber(value, maximumFractionDigits = 10) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return numeric.toLocaleString('ru-RU', { maximumFractionDigits });
}

function PulsePreview({ mode, value, unit }) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  let weight = mode === 'pulses_per_unit' ? 1 / numeric : numeric;
  let normalizedUnit = unit;
  if (unit === 'l') {
    weight /= 1000;
    normalizedUnit = 'm3';
  }
  const unitText = normalizedUnit === 'm3' ? 'м³' : unitLabels[normalizedUnit];
  const example = weight * 100;
  return (
    <Alert severity="info" icon={false} sx={{ py: 0.6, borderRadius: 2.25 }}>
      Будет сохранено: <strong>{formatNumber(weight)} {unitText} за импульс</strong>
      <Typography component="span" sx={{ display: 'block', mt: 0.35, fontSize: 12, color: '#526579' }}>
        Пример: +100 импульсов → +{formatNumber(example, 4)} {unitText}
      </Typography>
    </Alert>
  );
}

function ChannelPicker({ channels, value, onChange, disabled }) {
  if (!channels?.length) {
    return <Alert severity="warning" sx={{ borderRadius: 2.25 }}>Нет свободных каналов на этом модуле.</Alert>;
  }
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 0.8,
        gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))',
      }}
    >
      {channels.map((channel) => {
        const selected = String(channel.number) === String(value);
        return (
          <Button
            key={channel.id}
            disabled={disabled}
            onClick={() => onChange(String(channel.number))}
            sx={{
              minWidth: 0,
              py: 1.1,
              borderRadius: 2.25,
              textTransform: 'none',
              fontWeight: 900,
              boxShadow: 'none',
              border: '1px solid',
              borderColor: selected ? 'rgba(46, 115, 184, 0.55)' : 'rgba(38, 65, 94, 0.12)',
              background: selected
                ? 'linear-gradient(180deg, #2E73B8 0%, #2563A8 100%)'
                : 'linear-gradient(180deg, #FFFFFF 0%, #F7F9FC 100%)',
              color: selected ? '#fff' : '#1F3550',
              '&:hover': {
                background: selected
                  ? 'linear-gradient(180deg, #2E73B8 0%, #2563A8 100%)'
                  : 'linear-gradient(180deg, #F8FBFF 0%, #EEF5FF 100%)',
                boxShadow: 'none',
              },
            }}
          >
            {channel.number}
          </Button>
        );
      })}
    </Box>
  );
}

export default function PulseRadioModuleDialog({ open, device, buildingId, onClose, onChanged, embedded = false }) {
  const [modules, setModules] = useState([]);
  const [selectedModuleDetail, setSelectedModuleDetail] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [createMode, setCreateMode] = useState(false);
  const [rebindMode, setRebindMode] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [moduleForm, setModuleForm] = useState(emptyModule);
  const [selectedModuleId, setSelectedModuleId] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('');
  const [configureCoefficient, setConfigureCoefficient] = useState(true);
  const [bindingForm, setBindingForm] = useState({
    pulse_input_mode: 'units_per_pulse',
    pulse_input_value: '',
    pulse_input_unit: 'l',
    baseline_pulse_count: '',
    baseline_meter_value: '',
    coefficient_source: 'passport',
    notes: '',
  });

  const activeConnection = connections.find((item) => !item.active_to);
  const historyConnections = connections.filter((item) => item.active_to);
  const selectedModule = selectedModuleDetail?.id === selectedModuleId
    ? selectedModuleDetail
    : modules.find((item) => item.id === selectedModuleId);
  const freeChannels = useMemo(
    () => (selectedModule?.channels || []).filter((channel) => channel.is_active && !channel.device),
    [selectedModule]
  );

  const loadData = useCallback(async () => {
    if (!device?.id || !buildingId) return;
    setLoading(true);
    setError('');
    try {
      const [modulesPage, connectionItems] = await Promise.all([
        communicationEquipmentApi.listRadioModules(buildingId, { detail: 'summary', page_size: 100 }),
        communicationEquipmentApi.listDeviceConnections(device.id),
      ]);
      const moduleItems = modulesPage.items || [];
      setModules(moduleItems);
      setConnections(connectionItems);
      const firstAvailable = moduleItems.find((item) => item.occupied_channels < item.channel_count);
      const nextModuleId = firstAvailable?.id || moduleItems[0]?.id || '';
      setSelectedModuleId(nextModuleId);
      setCreateMode(moduleItems.length === 0);
      setRebindMode(false);
      setCloseReason('');
      setSelectedModuleDetail(null);
      if (nextModuleId) {
        const detail = await communicationEquipmentApi.getRadioModule(nextModuleId);
        setSelectedModuleDetail(detail);
        const firstChannel = detail.channels.find((channel) => channel.is_active && !channel.device);
        setSelectedChannel(firstChannel ? String(firstChannel.number) : '');
      } else {
        setSelectedChannel('');
      }
    } catch (loadError) {
      setError(loadError?.response?.data?.detail || 'Не удалось загрузить подключения прибора.');
    } finally {
      setLoading(false);
    }
  }, [buildingId, device?.id]);

  useEffect(() => {
    if (!open) return;
    setBindingForm({
      pulse_input_mode: 'units_per_pulse',
      pulse_input_value: '',
      pulse_input_unit: defaultUnit(device),
      baseline_pulse_count: '',
      baseline_meter_value: device?.latest_reading_value ?? '',
      coefficient_source: 'passport',
      notes: '',
    });
    setConfigureCoefficient(true);
    setModuleForm(emptyModule);
    loadData();
  }, [open, device, loadData]);

  useEffect(() => {
    if (!selectedModuleId || loading) return;
    let cancelled = false;
    (async () => {
      try {
        const detail = await communicationEquipmentApi.getRadioModule(selectedModuleId);
        if (cancelled) return;
        setSelectedModuleDetail(detail);
        const firstChannel = detail.channels.find((channel) => channel.is_active && !channel.device);
        setSelectedChannel((current) => {
          if (current && detail.channels.some((channel) => String(channel.number) === current && !channel.device)) {
            return current;
          }
          return firstChannel ? String(firstChannel.number) : '';
        });
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError?.response?.data?.detail || 'Не удалось загрузить каналы радиомодуля.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedModuleId, loading]);

  const updateModule = (field) => (event) => {
    setModuleForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const updateBinding = (field) => (event) => {
    setBindingForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleCreateModule = async () => {
    setSaving(true);
    setError('');
    try {
      const created = await communicationEquipmentApi.createRadioModule({
        building_id: buildingId,
        ...moduleForm,
        channel_count: Number(moduleForm.channel_count),
      });
      setModules((current) => [...current, created]);
      setSelectedModuleDetail(created);
      setSelectedModuleId(created.id);
      setSelectedChannel(String(created.channels[0]?.number || ''));
      setCreateMode(false);
    } catch (createError) {
      setError(createError?.response?.data?.detail || 'Не удалось создать радиомодуль.');
    } finally {
      setSaving(false);
    }
  };

  const handleBind = async () => {
    if (!selectedModuleId || !selectedChannel) return;
    if ((activeConnection || rebindMode) && !closeReason.trim()) {
      setError('Укажите причину закрытия текущего подключения.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        device_id: device.id,
        baseline_pulse_count: bindingForm.baseline_pulse_count === '' ? undefined : Number(bindingForm.baseline_pulse_count),
        baseline_meter_value: bindingForm.baseline_meter_value === '' ? undefined : Number(bindingForm.baseline_meter_value),
        notes: bindingForm.notes || undefined,
        replace_active: Boolean(activeConnection || rebindMode),
        close_reason: (activeConnection || rebindMode) ? closeReason.trim() : undefined,
      };
      if (configureCoefficient) {
        payload.pulse_input_mode = bindingForm.pulse_input_mode;
        payload.pulse_input_value = Number(bindingForm.pulse_input_value);
        payload.pulse_input_unit = bindingForm.pulse_input_unit;
        payload.coefficient_source = bindingForm.coefficient_source;
      }
      await communicationEquipmentApi.bindDevice(selectedModuleId, Number(selectedChannel), payload);
      await loadData();
      onChanged?.();
    } catch (bindError) {
      setError(bindError?.response?.data?.detail || 'Не удалось подключить прибор к каналу.');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseConnection = async () => {
    if (!activeConnection?.id || !closeReason.trim()) {
      setError('Укажите причину отключения.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await communicationEquipmentApi.closeConnection(activeConnection.id, closeReason.trim());
      await loadData();
      onChanged?.();
    } catch (closeError) {
      setError(closeError?.response?.data?.detail || 'Не удалось отключить прибор.');
    } finally {
      setSaving(false);
    }
  };

  const moduleChannelCount = Number(moduleForm.channel_count);
  const moduleFormValid = Boolean(
    moduleForm.name.trim() &&
    Number.isInteger(moduleChannelCount) && moduleChannelCount >= 1 && moduleChannelCount <= 64 &&
    (moduleForm.uplink_transport !== 'lorawan' || moduleForm.dev_eui.trim()) &&
    (moduleForm.uplink_transport !== 'mqtt' || moduleForm.network_address.trim())
  );
  const bindingValid = Boolean(
    selectedModuleId && selectedChannel &&
    (!configureCoefficient || Number(bindingForm.pulse_input_value) > 0) &&
    (!(activeConnection || rebindMode) || closeReason.trim())
  );

  const showBindForm = !activeConnection || rebindMode;

  const body = (
        loading ? (
          <Box sx={{ minHeight: 260, display: 'grid', placeItems: 'center' }}><CircularProgress size={30} /></Box>
        ) : (
          <Stack spacing={1.5}>
            {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}

            {['ХВС', 'ГВС'].includes(String(device?.system_type || '').trim()) ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Для ХВС/ГВС есть отдельная вкладка «Накладка» (1 радиомодуль = 1 счётчик).
                Импульсный регистратор с каналами — другой сценарий; нельзя совмещать с накладкой.
              </Alert>
            ) : null}

            {activeConnection ? (
              <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 3, borderColor: 'rgba(15, 159, 110, 0.28)', boxShadow: 'none' }}>
                <Stack spacing={1.1}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: '#1F3550' }}>Подключение активно</Typography>
                      <Typography variant="body2" sx={{ color: '#526579' }}>
                        {activeConnection.module_name} · канал {activeConnection.channel_number}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                      <Chip size="small" color="success" variant="outlined" label="Подключено" />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={activeConnection.pulse_weight
                          ? `${formatNumber(activeConnection.pulse_weight)} ${activeConnection.pulse_unit || ''}/имп.`
                          : 'Вес импульса не настроен'}
                      />
                    </Stack>
                  </Stack>
                  <TextField
                    fullWidth
                    size="small"
                    label="Причина отключения / переключения"
                    value={closeReason}
                    onChange={(event) => setCloseReason(event.target.value)}
                    placeholder="Замена канала, ошибка монтажа, поверка…"
                  />
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button size="small" variant="outlined" color="warning" disabled={saving || !closeReason.trim()} onClick={handleCloseConnection} sx={{ textTransform: 'none', borderRadius: 2 }}>
                      Отключить
                    </Button>
                    <Button size="small" variant="outlined" disabled={saving} onClick={() => setRebindMode((value) => !value)} sx={{ textTransform: 'none', borderRadius: 2 }}>
                      {rebindMode ? 'Отменить переключение' : 'Переключить канал'}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            ) : null}

            {historyConnections.length ? (
              <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 3, boxShadow: 'none' }}>
                <Typography sx={{ mb: 1, fontWeight: 900, color: '#1F3550' }}>История подключений</Typography>
                <Stack spacing={0.7}>
                  {historyConnections.slice(0, 5).map((item) => (
                    <Typography key={item.id} variant="body2" sx={{ color: '#526579' }}>
                      {item.module_name} · канал {item.channel_number}
                      {' · '}
                      {new Date(item.active_from).toLocaleString('ru-RU')}
                      {' → '}
                      {item.active_to ? new Date(item.active_to).toLocaleString('ru-RU') : '…'}
                      {item.close_reason ? ` · ${item.close_reason}` : ''}
                    </Typography>
                  ))}
                </Stack>
              </Paper>
            ) : null}

            {showBindForm ? (
              <>
                <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 3, boxShadow: 'none' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: createMode ? 1.4 : 0 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: '#1F3550' }}>
                        {rebindMode ? 'Новый канал' : 'Радиомодуль'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>Выберите существующий или добавьте новый</Typography>
                    </Box>
                    <Button
                      size="small"
                      variant={createMode ? 'contained' : 'outlined'}
                      startIcon={<AddRoundedIcon />}
                      onClick={() => setCreateMode((value) => !value)}
                      sx={{ textTransform: 'none', borderRadius: 2, boxShadow: 'none', fontWeight: 800 }}
                    >
                      Новый модуль
                    </Button>
                  </Stack>

                  {createMode ? (
                    <Stack spacing={1.2}>
                      <Divider />
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={7}>
                          <TextField fullWidth size="small" label="Название" value={moduleForm.name} onChange={updateModule('name')} placeholder="Подъезд 1, шкаф А" />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <TextField select fullWidth size="small" label="Передача данных" value={moduleForm.uplink_transport} onChange={updateModule('uplink_transport')}>
                            <MenuItem value="lorawan">LoRaWAN</MenuItem>
                            <MenuItem value="mqtt">MQTT</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size="small" label="Производитель" value={moduleForm.manufacturer} onChange={updateModule('manufacturer')} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size="small" label="Модель" value={moduleForm.model} onChange={updateModule('model')} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size="small" label="Серийный номер" value={moduleForm.serial_number} onChange={updateModule('serial_number')} />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          {moduleForm.uplink_transport === 'lorawan' ? (
                            <TextField fullWidth size="small" label="DevEUI" value={moduleForm.dev_eui} onChange={updateModule('dev_eui')} inputProps={{ maxLength: 23 }} />
                          ) : (
                            <TextField fullWidth size="small" label="MQTT-топик" value={moduleForm.network_address} onChange={updateModule('network_address')} />
                          )}
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size="small" type="number" label="Количество каналов" value={moduleForm.channel_count} onChange={updateModule('channel_count')} inputProps={{ min: 1, max: 64, step: 1 }} />
                        </Grid>
                      </Grid>
                      <Box><Button variant="contained" disabled={!moduleFormValid || saving} onClick={handleCreateModule} startIcon={<RouterRoundedIcon />} sx={{ textTransform: 'none', borderRadius: 2, boxShadow: 'none', fontWeight: 800 }}>Создать радиомодуль</Button></Box>
                    </Stack>
                  ) : null}
                </Paper>

                {!createMode || modules.length ? (
                  <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 3, boxShadow: 'none' }}>
                    <Typography sx={{ mb: 1.1, fontWeight: 900, color: '#1F3550' }}>Канал подключения</Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextField select fullWidth size="small" label="Радиомодуль" value={selectedModuleId} onChange={(event) => setSelectedModuleId(event.target.value)}>
                          {modules.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.name} · {item.occupied_channels}/{item.channel_count} занято
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 0.7 }}>
                          Свободный канал
                        </Typography>
                        <ChannelPicker
                          channels={freeChannels}
                          value={selectedChannel}
                          onChange={setSelectedChannel}
                          disabled={!freeChannels.length}
                        />
                      </Grid>
                    </Grid>
                    {selectedModule && !freeChannels.length ? <Alert severity="warning" sx={{ mt: 1 }}>У выбранного радиомодуля нет свободных каналов.</Alert> : null}
                  </Paper>
                ) : null}

                <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 3, boxShadow: 'none' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: '#1F3550' }}>Пересчёт импульсов</Typography>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>Значение берётся из паспорта или маркировки счётчика</Typography>
                    </Box>
                    <FormControlLabel control={<Switch checked={configureCoefficient} onChange={(event) => setConfigureCoefficient(event.target.checked)} />} label="Настроить" />
                  </Stack>
                  {configureCoefficient ? (
                    <Stack spacing={1.1} sx={{ mt: 1.2 }}>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={4}>
                          <TextField select fullWidth size="small" label="Формат" value={bindingForm.pulse_input_mode} onChange={updateBinding('pulse_input_mode')}>
                            <MenuItem value="units_per_pulse">1 импульс = значение</MenuItem>
                            <MenuItem value="pulses_per_unit">Импульсов на единицу</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size="small" type="number" label={bindingForm.pulse_input_mode === 'units_per_pulse' ? 'Значение за импульс' : 'Количество импульсов'} value={bindingForm.pulse_input_value} onChange={updateBinding('pulse_input_value')} inputProps={{ min: 0, step: 'any' }} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField select fullWidth size="small" label="Единица" value={bindingForm.pulse_input_unit} onChange={updateBinding('pulse_input_unit')}>
                            {Object.entries(unitLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                          </TextField>
                        </Grid>
                      </Grid>
                      <PulsePreview mode={bindingForm.pulse_input_mode} value={bindingForm.pulse_input_value} unit={bindingForm.pulse_input_unit} />
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size="small" type="number" label="Импульсов на момент подключения" value={bindingForm.baseline_pulse_count} onChange={updateBinding('baseline_pulse_count')} inputProps={{ min: 0, step: 1 }} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size="small" type="number" label="Показание прибора" value={bindingForm.baseline_meter_value} onChange={updateBinding('baseline_meter_value')} inputProps={{ min: 0, step: 'any' }} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField select fullWidth size="small" label="Источник" value={bindingForm.coefficient_source} onChange={updateBinding('coefficient_source')}>
                            {sourceOptions.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                          </TextField>
                        </Grid>
                      </Grid>
                    </Stack>
                  ) : (
                    <Alert severity="warning" sx={{ mt: 1 }}>Связь сохранится, но автоматический пересчёт показаний будет отключён до настройки веса импульса.</Alert>
                  )}
                </Paper>
              </>
            ) : null}
          </Stack>
        )
  );

  const actions = (
    <>
      <Button onClick={onClose} disabled={saving}>Закрыть</Button>
      {!loading && showBindForm ? (
        <Button variant="contained" onClick={handleBind} disabled={!bindingValid || saving} sx={{ textTransform: 'none', borderRadius: 2, boxShadow: 'none', fontWeight: 850 }}>
          {saving ? 'Сохранение…' : (rebindMode ? 'Переключить канал' : 'Подключить прибор')}
        </Button>
      ) : null}
    </>
  );

  if (embedded) {
    if (!open) return null;
    return (
      <Box>
        {body}
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
          {actions}
        </Stack>
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CableRoundedIcon sx={{ color: '#2E73B8' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#1F3550', lineHeight: 1.15 }}>
              Подключение прибора
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              {device?.system_type || 'Прибор'} · {device?.serial_number || 'без серийного номера'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ background: '#F7F9FC' }}>
        {body}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        {actions}
      </DialogActions>
    </Dialog>
  );
}
